import {
    useMutation,
    useQueryClient,
    useQuery,
    QueryOptions,
    UseMutationResult,
    QueryResult,
} from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type { AccountNumber, Receiver, ReceiverId, ReceiverRequest } from '../generated-types';
import { createReceiver, deleteReceiver, getReceivers } from '../services/receiverService';
import sortByKey from '../utils/mapper/sortByKey';

const queryKey = ['receivers'];

const useReceivers = (options: Partial<QueryOptions<Receiver[]>> = {}): QueryResult<Receiver[]> =>
    useQuery({
        ...options,
        queryKey,
        queryFn: getReceivers,
        select: (data) => {
            data.sort(sortByKey('name'));
            return data;
        },
    });

export const useSingleReceiver = (accountNumber: AccountNumber | undefined, enabled = true) => {
    const { data: receivers, isLoading, isFetched } = useReceivers({ enabled: !!accountNumber && enabled });
    const data = receivers?.find((receiver) => receiver.accountNumber.value === accountNumber?.value);
    return { data, isLoading, isFetched };
};

type UseMutateReceiversResult = {
    add: UseMutationResult<Receiver, RequestExceptionWithResponse, ReceiverRequest>;
    remove: UseMutationResult<
        Receiver,
        RequestExceptionWithResponse,
        ReceiverId,
        {
            backup: Receiver[] | undefined;
        }
    >;
    invalidate: () => void;
};

export const useMutateReceivers = (): UseMutateReceiversResult => {
    const queryClient = useQueryClient();
    const add = useMutation<Receiver, RequestExceptionWithResponse, ReceiverRequest>({
        mutationFn: (receiver: ReceiverRequest) => createReceiver(receiver),
        onSuccess: async (receiver: Receiver) => {
            queryClient.setQueryData<Receiver[]>(queryKey, (receivers = []) => [...receivers, receiver]);
        },
    });

    const remove = useMutation<
        Receiver,
        RequestExceptionWithResponse,
        ReceiverId,
        {
            backup: Receiver[] | undefined;
        }
    >({
        mutationFn: (receiverId) => deleteReceiver(receiverId),
        onMutate: async (receiverId) => {
            await queryClient.cancelQueries({ queryKey });
            const backup = queryClient.getQueryData<Receiver[]>(queryKey);
            queryClient.setQueryData<Receiver[]>(queryKey, (receivers = []) =>
                receivers.filter((receiver) => receiver.id.value !== receiverId.value),
            );
            return { backup };
        },
        onError: (_err, _mutateRequest, context) => {
            queryClient.setQueryData(queryKey, context?.backup);
        },
    });

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey });
    };

    return { add, remove, invalidate };
};

export default useReceivers;
